/**
 * @module components/SanityBadge
 * @description Styled badge component with Sanity CMS tone colors.
 * Displays labels with consistent styling and optional icons, supporting outline variants.
 */

import React from 'react';
import { Sanity } from 'src/types';
import { SanityBadgeProps } from './types';
import * as Styles from './styles';

/**
 * @component SanityBadge
 * @summary Customizable badge with tone-based colors and optional icon.
 * Uses Sanity CMS tone system (default, primary, positive, caution, critical) for consistent UI.
 * @type {React.FC<SanityBadgeProps>}
 * @param {SanityBadgeProps} props - Component props from types.ts
 * @returns {React.ReactElement} Styled badge with text and optional icon
 */
export const SanityBadge = ({
  className,
  children,
  tone = Sanity.Types.SanityTone.DEFAULT,
  outline = false,
  icon,
  ...rest
}: SanityBadgeProps) => {
  return (
    <Styles.SanityBadge className={className} $tone={tone} $outline={outline} {...rest}>
      {icon && <Styles.BadgeIcon>{icon}</Styles.BadgeIcon>}
      <Styles.BadgeText>{children}</Styles.BadgeText>
    </Styles.SanityBadge>
  );
};
